/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ArrowRightXsIcon,
  ContentAllowedIcon,
  ContentBlockedIcon,
  ContentIgnoreIcon,
  ContentMixedIcon,
  ContentWarningIcon,
  PolicyLockIcon,
  RenderIf,
  ResetIcon,
  TooltipBody,
  TooltipWrapper,
  UnlockIcon,
} from 'components'
import classNames from 'classnames'
import {
  BlockPageList,
  BlockPageType,
  CategoriesV2Type,
  CATEGORY_RESTRICTIONS,
  ChildCategoryV2Type,
  PolicyInheritTagState,
  SearchUsersGroupsItem,
  UnknownObject,
  UsersGroupsOption,
} from 'interfaces'
import {usePolicy} from 'hooks'
import {categoryType, EnterTab, noOperation} from 'definitions/constant'
import {FC} from 'react'
import {dispatch} from 'app/store'
import {policiesActions} from './policiesSlice'
import {PolicyUpdateField} from 'interfaces/Policy'
import {CanIAccess, PERMISSIONS} from 'rbac'
import {POLICY_INHERITANCE_TAG_CLASSES} from './policyTagStyles'

export const defaultCloudApplication = 'defaultcloudapplication'
export const salesforce = 'salesforce'
export const box = 'box'

const policyState = ['Allowed', 'Blocked', 'Warning', 'Ignore', 'Mixed']

export const customizedText = 'Customized'
export const inheritsBaseText = 'Inherits Base'
export const resetPolicyText = 'Reset Policy'
export const goToBasePolicyText = 'Go to Base Policy'
export const resetByPassText = 'Reset Bypass'
export const goToBaseBypassText = 'Go to Base Bypass'
export const resetToBaseText = 'Reset to Base'
export const goToBaseText = 'Go to Base'

/**
 * Determines the collective restriction state of child categories.
 *
 * @param {ChildCategoryV2Type} childCategories - The child categories to evaluate.
 * @returns {number} - The collective restriction state. Returns 4 if no collective restriction is found.
 */
const childCategoriesCollectiveRestriction = (
  childCategories: ChildCategoryV2Type,
): number => {
  const restrictionWiseCategoryCount = Object.values(childCategories).reduce(
    (acc: UnknownObject, category) => {
      acc[category.state] =
        ++acc[category.state] || CATEGORY_RESTRICTIONS.Blocked
      return acc
    },
    {},
  )

  const collectiveRestriction = Number(
    Object.keys(restrictionWiseCategoryCount).find(
      key =>
        restrictionWiseCategoryCount[key] ===
        Object.values(childCategories).length,
    ),
  )
  return isNaN(collectiveRestriction)
    ? CATEGORY_RESTRICTIONS.Mixed
    : collectiveRestriction
}

const hasChildCategories = (
  categoryName: string,
  categories: CategoriesV2Type | undefined,
): boolean => {
  const children = categories?.[categoryName]?.child_categories
  return Boolean(children && Object.keys(children).length > 0)
}

const getSubcategoryCountV2 = (
  categoryName: string,
  categories: CategoriesV2Type | undefined,
  searchTerm: string,
) => {
  const childCategories = categories?.[categoryName]?.child_categories
  if (childCategories) {
    return Object.keys(childCategories)?.filter(item =>
      categoryV2FilterCallback(item, searchTerm, {}),
    )?.length
  }
  return 0
}

// TODO Note from @aman-ds: This should be a component and not a function.
const policyStateIcon = (
  state?: number,
  onPolicyIconClick?: () => void,
  datacy: string = '',
  isDisabled: boolean = false,
  ariaLabel?: string,
) => {
  switch (state) {
    case 0:
      return (
        <span
          data-tooltip-id="policy-state-tooltip"
          data-tooltip-content="Allowed"
        >
          <ContentAllowedIcon
            onClick={onPolicyIconClick}
            classes={isDisabled ? 'opacity-50' : 'cursor-pointer'}
            datacy={`content-allowed-icon${datacy}`}
            ariaLabel={ariaLabel}
          />
        </span>
      )
    case 1:
      return (
        <span
          data-tooltip-id="policy-state-tooltip"
          data-tooltip-content="Blocked"
        >
          <ContentBlockedIcon
            onClick={onPolicyIconClick}
            classes={isDisabled ? 'opacity-50' : 'cursor-pointer'}
            datacy={`content-blocked-icon${datacy}`}
            ariaLabel={ariaLabel}
          />
        </span>
      )
    case 2:
      return (
        <span
          data-tooltip-id="policy-state-tooltip"
          data-tooltip-content="Warning"
        >
          <ContentWarningIcon
            onClick={onPolicyIconClick}
            classes={isDisabled ? 'opacity-50' : 'cursor-pointer'}
            datacy={`content-warning-icon${datacy}`}
            ariaLabel={ariaLabel}
          />
        </span>
      )
    case 3:
      return (
        <span
          data-tooltip-id="policy-state-tooltip"
          data-tooltip-content="Ignore"
        >
          <ContentIgnoreIcon
            onClick={onPolicyIconClick}
            classes={isDisabled ? 'opacity-50' : 'cursor-pointer'}
            datacy={datacy}
            ariaLabel={ariaLabel}
          />
        </span>
      )
    default:
      return (
        <span
          data-tooltip-id="policy-state-tooltip"
          data-tooltip-content="Mixed"
        >
          <ContentMixedIcon
            onClick={onPolicyIconClick}
            classes={isDisabled ? 'opacity-50' : 'cursor-pointer'}
            datacy={datacy}
            ariaLabel={ariaLabel}
          />
        </span>
      )
  }
}

const togglePolicyState = (
  state: number,
  type: string = categoryType.default,
) => {
  switch (state) {
    case 0:
      return 1
    case 1:
      return 2
    case 2:
      if (type === categoryType.custom) {
        return 3
      }
      return 0
    default:
      return 0
  }
}

interface InheritTagProps {
  tagState: PolicyInheritTagState
  goToBasePolicy: () => void
  resetToBase: () => void
}

export const InheritTag: FC<InheritTagProps> = ({
  tagState,
  goToBasePolicy,
  resetToBase,
}) => {
  const {isBasePolicy} = usePolicy()
  return (
    <>
      <button
        type="button"
        disabled={!tagState.goToBasePolicy}
        className={classNames(POLICY_INHERITANCE_TAG_CLASSES, 'ml-2', {
          hidden: isBasePolicy,
          'cursor-pointer': tagState.goToBasePolicy,
          'cursor-default': !tagState.goToBasePolicy,
        })}
        data-cy="bypass-go-to-base"
        onClick={goToBasePolicy}
      >
        <span
          className={classNames('flex items-center', {
            hidden: tagState.text === customizedText,
          })}
        >
          <PolicyLockIcon />
        </span>
        <span
          className={classNames('flex items-center', {
            hidden: tagState.text !== customizedText,
          })}
        >
          <UnlockIcon />
        </span>
        <span>{tagState.text}</span>
      </button>
      <CanIAccess reqPermissions={[PERMISSIONS.EDIT_POLICY.WRITE]}>
        <RenderIf isTrue={tagState.showReset}>
          <>
            <TooltipWrapper
              id="reset-all-overrides-tooltip"
              place="top"
              positionStrategy="fixed"
              noPadding
              offset={10}
            >
              <TooltipBody data-cy="default-categories">
                Reset All Overrides
              </TooltipBody>
            </TooltipWrapper>
            <button
              data-tooltip-id="reset-all-overrides-tooltip"
              onClick={resetToBase}
              data-cy="reset-to-base"
              className="flex justify-center items-center w-5 h-5 rounded-full hover:bg-dope-cyan-400 bg-dope-teal-300 ml-2 cursor-pointer group-hover"
            >
              <ResetIcon
                strokeWidth={0}
                classes="fill-current text-dope-cyan-bright hover:text-dope-cyan-300"
              />
            </button>
          </>
        </RenderIf>
      </CanIAccess>
    </>
  )
}

const formatGroupLabel = (data: SearchUsersGroupsItem) => (
  <div className="flex items-center justify-start">
    <span className="abc-bold text-xs text-dope-cyan-dark capitalize">
      {data?.label}
    </span>
  </div>
)

const getUserGroupsOptionLabel = (option: UsersGroupsOption): string => {
  const {name = '', emailId = ''} = option || {}
  return emailId ? `${name.trim()} (${emailId.trim()})` : ''
}

const blockPageListFilter = (
  state: number,
  blockPageList: BlockPageList[],
  categoryName: string,
): BlockPageList[] => {
  return blockPageList
    .filter(
      ele =>
        (policyState[state] === policyState[parseInt(ele.type)] &&
          ele.value !== BlockPageType.defaultCloudApplication &&
          ele.value !== BlockPageType.defaultSecurityRisk) ||
        (categoryName === 'Security' &&
          ele.value === BlockPageType.defaultSecurityRisk),
    )
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
}

type EnterTabTextProps = {
  condition: boolean
  spanClasses: string
  iconClasses: string
  buttonId?: string
  customStyle?: any // using any here as Style has many properties
  textColor?: string
  hideText?: boolean
  onButtonClick?: () => void
}

const EnterTabText: FC<EnterTabTextProps> = ({
  condition = false,
  spanClasses,
  iconClasses,
  customStyle,
  buttonId = 'submit-executable',
  textColor = 'text-gray-600',
  hideText = false,
  onButtonClick = noOperation(),
}) => {
  return (
    <RenderIf isTrue={Boolean(condition)}>
      <>
        <button
          type="submit"
          id={buttonId}
          onClick={() => (onButtonClick ? onButtonClick() : noOperation())}
        >
          <span
            className={classNames(spanClasses, textColor, {
              hidden: hideText,
            })}
            style={customStyle}
          >
            {EnterTab}
          </span>
          <ArrowRightXsIcon classes={iconClasses} strokeColor={textColor} />
        </button>
      </>
    </RenderIf>
  )
}

const updatePolicyField = (
  type: string,
  throwErrorValue: string,
  value: string = '',
  throwError: boolean = false,
  updatedPolicyField?: PolicyUpdateField,
): void => {
  dispatch(
    policiesActions.updatePolicyUpdateField({
      ...updatedPolicyField,
      [type]: value,
      [throwErrorValue]: throwError,
      throwError: throwError,
    }),
  )
}

const categoryV2FilterCallback = (
  categoryName: string,
  searchTerm: string,
  subCategories: ChildCategoryV2Type = {},
): boolean => {
  const subCategoryKeys = Object.keys(subCategories)
  if (!searchTerm) {
    return true
  } else if (categoryName?.toLowerCase()?.includes(searchTerm?.toLowerCase())) {
    return true
  } else {
    return (
      subCategoryKeys?.length > 0 &&
      subCategoryKeys.some(subItem =>
        subItem?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
      )
    )
  }
}

export {
  policyState,
  policyStateIcon,
  childCategoriesCollectiveRestriction,
  togglePolicyState,
  hasChildCategories,
  getSubcategoryCountV2,
  formatGroupLabel,
  getUserGroupsOptionLabel,
  blockPageListFilter,
  EnterTabText,
  updatePolicyField,
  categoryV2FilterCallback,
}
